import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  gamificationApi,
  pathwayApi,
  type MissionAction,
  type WeeklyMissionTask,
  type WeeklyMissionPayload,
} from '../api';
import { missionPayloadFrom } from '../missionEvents';
import { sortMissionTasks } from './missionModel';
import MissionWorkspace from '../platforms/web/desktop/MissionWorkspace';
import { Settings } from 'lucide-react';

interface Props {
  initialTaskId?: string;
  initialDimension?: string;
  onClose: () => void;
  onOpenAction?: (action: MissionAction) => void;
  onMissionUpdated?: (mission: WeeklyMissionPayload) => void;
}

const MissionCenter: React.FC<Props> = ({ initialTaskId, initialDimension, onOpenAction, onMissionUpdated }) => {
  const [mission, setMission] = useState<WeeklyMissionPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const inFlight = useRef(new Set<string>());
  const requestSequence = useRef(0);
  const savingRef = useRef(false);
  const updateCallback = useRef(onMissionUpdated);
  updateCallback.current = onMissionUpdated;
  const [profile, setProfile] = useState({ age: '', height_cm: '', weight_kg: '', health_notes: '' });
  const [showSettings, setShowSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>([]);
  const [bodyFocus, setBodyFocus] = useState('fitness');
  const [bodyDifficulty, setBodyDifficulty] = useState('moderate');
  const [dailyMinutes, setDailyMinutes] = useState(30);
  const [weeklyTaskLimit, setWeeklyTaskLimit] = useState(7);
  const total = mission?.total ?? 0;
  const completed = mission?.completed ?? 0;
  const pct = Math.max(0, Math.min(100, mission?.pct ?? 0));
  const config = mission?.config;
  const bodyContext = mission?.body_context;
  const report = mission?.weekly_report;
  const snapshot = mission?.source_snapshot;
  const groups = (mission?.groups || []).map(group => ({ ...group, tasks: sortMissionTasks(group.tasks) }))
    .sort((a, b) => Number(a.tasks.every(task => task.status === 'completed')) - Number(b.tasks.every(task => task.status === 'completed')));
  const dailyStudy = [...(mission?.daily_study || [])].sort((a, b) => Number(a.status === 'completed') - Number(b.status === 'completed'));

  const syncDraft = useCallback((data: WeeklyMissionPayload) => {
    const settings = data.config;
    setProfile({ age: String(settings.profile?.age ?? ''), height_cm: String(settings.profile?.height_cm ?? ''),
      weight_kg: String(settings.profile?.weight_kg ?? ''), health_notes: settings.profile?.health_notes || '' });
    setSelectedDimensions(settings.selected_dimensions || []);
    setBodyFocus(settings.body_focus || 'fitness');
    setBodyDifficulty(settings.body_difficulty || 'moderate');
    setDailyMinutes(settings.daily_minutes ?? 30);
    setWeeklyTaskLimit(settings.weekly_task_limit ?? 7);
  }, []);

  const applyMission = useCallback((data: WeeklyMissionPayload, updateDraft = false) => {
    setMission(data);
    setError(null);
    if (updateDraft) syncDraft(data);
    updateCallback.current?.(data);
  }, [syncDraft]);

  const load = useCallback(async (quiet = false, updateDraft = false) => {
    const sequence = ++requestSequence.current;
    if (quiet) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await gamificationApi.getWeeklyMission();
      if (sequence === requestSequence.current) { applyMission(res.data, updateDraft); return true; }
    } catch (e: any) {
      if (sequence === requestSequence.current) {
        const message = e?.response?.data?.detail || '本周任务加载失败';
        if (quiet) setNotice(message);
        else setError(message);
      }
    } finally {
      if (sequence === requestSequence.current) { setLoading(false); setRefreshing(false); }
    }
    return false;
  }, [applyMission]);

  useEffect(() => {
    load(false, true);
    return () => { requestSequence.current += 1; };
  }, [load]);

  const beginAction = (key: string) => {
    if (savingRef.current || inFlight.current.has(key)) return false;
    setRefreshing(false);
    inFlight.current.add(key);
    setBusy(current => ({ ...current, [key]: true }));
    setNotice(null);
    return true;
  };
  const endAction = (key: string) => {
    inFlight.current.delete(key);
    setBusy(current => ({ ...current, [key]: false }));
  };

  const handleComplete = async (task: WeeklyMissionTask) => {
    if (task.verification === 'auto' || task.archived || task.status === 'completed') return;
    const key = `weekly:${task.id}`;
    if (!beginAction(key)) return;
    const sequence = ++requestSequence.current;
    try {
      const response = await gamificationApi.completeWeeklyMissionTask(task.id);
      const payload = missionPayloadFrom(response.data);
      if (payload && sequence === requestSequence.current) {
        applyMission(payload);
        setNotice('已确认完成，进度已更新');
      } else if (await load(true)) setNotice('已确认完成，进度已更新');
    } catch (e: any) {
      setNotice(e?.response?.data?.detail || '完成任务失败，请重试');
    } finally { endAction(key); }
  };

  const toggleDimension = (key: string) => {
    if (key === 'body') return;
    setSelectedDimensions(current => current.includes(key) ? current.filter(item => item !== key) : [...current, key]);
  };

  const handleGenerate = async () => {
    if (savingRef.current || inFlight.current.size) return;
    if (selectedDimensions.length === 0) { setNotice('请至少选择一个任务维度'); return; }
    const values: Array<[string, string, number, number]> = [
      ['年龄', profile.age, 13, 110], ['身高', profile.height_cm, 100, 230], ['体重', profile.weight_kg, 25, 300],
    ];
    const invalid = values.find(([, value, min, max]) => value !== '' && (!Number.isFinite(Number(value)) || Number(value) < min || Number(value) > max));
    if (invalid) { setNotice(`${invalid[0]}请填写 ${invalid[2]}–${invalid[3]} 之间的数字，或留空`); return; }
    savingRef.current = true;
    requestSequence.current += 1;
    setRefreshing(false); setSaving(true); setNotice(null);
    try {
      const response = await gamificationApi.generateWeeklyMission({
        selected_dimensions: selectedDimensions, body_focus: bodyFocus, body_difficulty: bodyDifficulty,
        daily_minutes: dailyMinutes, weekly_task_limit: weeklyTaskLimit,
        profile: { age: profile.age ? Number(profile.age) : null, height_cm: profile.height_cm ? Number(profile.height_cm) : null,
          weight_kg: profile.weight_kg ? Number(profile.weight_kg) : null, health_notes: profile.health_notes },
      });
      requestSequence.current += 1;
      applyMission(response.data, true);
      setNotice('任务已更新；自动任务会根据本周实际记录更新进度，已完成记录保留');
      setShowSettings(false);
    } catch (e: any) {
      if (e?.code === 'ECONNABORTED') {
        setNotice('生成响应超时，正在核对当前任务；确认结果前请不要重复生成');
        await load(true, true);
        setNotice('已尝试同步当前任务，请检查列表；若未更新，可稍后重试生成');
      } else setNotice(e?.response?.data?.detail || '任务生成失败，请稍后重试');
    } finally { savingRef.current = false; setSaving(false); }
  };

  const completeStudy = async (pathwayId: string, taskId: string) => {
    const key = `study:${pathwayId}:${taskId}`;
    if (!beginAction(key)) return;
    requestSequence.current += 1;
    try {
      await pathwayApi.completeDailyTask(pathwayId, taskId);
      if (await load(true)) setNotice('学习完成状态已记录，本周学习天数已同步');
    } catch (e: any) { setNotice(e?.response?.data?.detail || '学习任务完成失败'); }
    finally { endAction(key); }
  };

  return (
    <div className="dim-root desktop-missions">
      <header className="desktop-page-header">
        <div><h1>本周任务</h1><p>{mission?.week_start ? (() => { const start = new Date(`${mission.week_start}T12:00:00`); const end = new Date(start); end.setDate(end.getDate() + 6); const fmt = (d: Date) => d.toLocaleDateString('zh-CN', {month:'long',day:'numeric'}); return `${fmt(start)} — ${fmt(end)}`; })() : '用小行动，积累真实的成长。'}</p></div>
        {mission && <div className="desktop-progress-summary"><strong>{completed} / {total}</strong><span>已完成</span><progress aria-label="本周完成进度" max={100} value={pct}/><small>{Math.round(pct)}%</small></div>}
        <button type="button" className="desktop-secondary" disabled={saving || refreshing} onClick={() => { if (mission) syncDraft(mission); setShowSettings(value => !value); }}><Settings size={18}/>{showSettings ? '取消' : '任务设置'}</button>
      </header>

      <main className="dim-main">
        {loading ? (
          <div className="dim-loading"><div className="dim-spinner" /><p>加载中...</p></div>
        ) : error ? (
          <div className="dim-empty"><p>{error}</p><button type="button" className="ms-done-btn" onClick={() => load(false, true)}>重试</button></div>
        ) : (
          <>
            {notice && <div className="ms-notice" role="status">{notice}</div>}
            {refreshing && <div className="ms-refreshing" role="status">正在同步任务进度…</div>}
            {showSettings && config && (
              <fieldset className="ms-config-panel" disabled={saving}>
                <div className="ms-config-kicker">PERSONAL MISSION</div>
                <h2>定制本周任务</h2>
                <p className="ms-config-intro">按每天可投入的时间安排任务。自动任务以真实饮食、运动、普通笔记和途径学习记录计算进度；重新生成保留已完成记录。</p>

                <label className="ms-field-label">每天可用时间</label>
                <div className="ms-difficulty-row">{[15, 30, 60].map(value => <button type="button" key={value} aria-pressed={dailyMinutes === value} className={dailyMinutes === value ? 'selected' : ''} onClick={() => setDailyMinutes(value)}>{value} 分钟</button>)}</div>
                <label className="ms-field-label">本周任务上限</label>
                <div className="ms-difficulty-row">{[5, 7, 10].map(value => <button type="button" key={value} aria-pressed={weeklyTaskLimit === value} className={weeklyTaskLimit === value ? 'selected' : ''} onClick={() => setWeeklyTaskLimit(value)}>{value} 项</button>)}</div>
                <label className="ms-field-label">任务维度</label>
                <div className="ms-dimension-grid">
                  {config.dimension_options.map(option => {
                    const selected = selectedDimensions.includes(option.key);
                    return (
                      <button
                        key={option.key}
                        type="button"
                        className={`ms-dimension-option ${selected ? 'selected' : ''}`}
                        style={selected ? { borderColor: option.color, color: option.color } : undefined}
                        onClick={() => toggleDimension(option.key)}
                        disabled={option.key === 'body'}
                      >
                        <span>{option.icon}</span><strong>{option.name}</strong>
                      </button>
                    );
                  })}
                </div>

                {selectedDimensions.includes('body') && (
                  <div className="ms-body-config">
                    <div className="ms-body-heading">
                      <div><span>身体任务</span><strong>健康适配</strong></div>
                      <small>系统只会根据健康状态降低风险，不会强行提高强度</small>
                    </div>
                    <label className="ms-field-label">训练方向</label>
                    <div className="ms-focus-grid">
                      {config.body_focus_options.map(option => (
                        <button
                          key={option.key}
                          type="button"
                          className={`ms-choice-card ${bodyFocus === option.key ? 'selected' : ''}`}
                          onClick={() => setBodyFocus(option.key)}
                        >
                          <strong>{option.name}</strong>
                          <span>{option.description}</span>
                        </button>
                      ))}
                    </div>
                    <label className="ms-field-label">期望难度</label>
                    <div className="ms-difficulty-row">
                      {config.body_difficulty_options.map(option => (
                        <button
                          key={option.key}
                          type="button"
                          className={bodyDifficulty === option.key ? 'selected' : ''}
                          onClick={() => setBodyDifficulty(option.key)}
                        >{option.name}</button>
                      ))}
                    </div>
                    <label className="ms-field-label">健康资料（可留空，系统会标注默认假设）</label>
                    <div className="ms-profile-fields">
                      <input type="number" min="13" max="110" placeholder="年龄" aria-label="年龄" value={profile.age} onChange={e => setProfile(p => ({ ...p, age: e.target.value }))} />
                      <input type="number" min="100" max="230" placeholder="身高 cm" aria-label="身高" value={profile.height_cm} onChange={e => setProfile(p => ({ ...p, height_cm: e.target.value }))} />
                      <input type="number" min="25" max="300" placeholder="体重 kg" aria-label="体重" value={profile.weight_kg} onChange={e => setProfile(p => ({ ...p, weight_kg: e.target.value }))} />
                    </div>
                    <input className="ms-health-notes" maxLength={300} placeholder="身体情况或运动限制（可选）" aria-label="身体情况" value={profile.health_notes} onChange={e => setProfile(p => ({ ...p, health_notes: e.target.value }))} />
                  </div>
                )}
                <button type="button" className="ms-generate-btn" disabled={saving || Object.values(busy).some(Boolean)} onClick={handleGenerate}>
                  {saving ? '正在生成...' : '按此设置生成任务'}
                </button>
              </fieldset>
            )}

            <MissionWorkspace initialTaskId={initialTaskId} initialDimension={initialDimension} mission={mission} busy={busy} saving={saving} onComplete={handleComplete} onOpenAction={onOpenAction}/>
            {dailyStudy.length > 0 && (
              <section className="ms-group">
                <div className="ms-group-hdr"><span>📚</span><strong>今日途径学习</strong><span className="ms-count">{dailyStudy.filter(item => item.status === 'completed').length}/{dailyStudy.length}</span></div>
                <div className="ms-tasks">{dailyStudy.map(item => (
                  <div key={`${item.pathway_id}:${item.task_id}`} className={`ms-task ${item.status === 'completed' ? 'done' : ''}`}>
                    <span>{item.status === 'completed' ? '✅' : '⬜'}</span>
                    <div className="ms-task-body"><div className="ms-task-title">{item.title}</div><div className="ms-task-desc">{item.description}</div></div>
                    {item.status !== 'completed' && <button type="button" className="ms-done-btn" disabled={saving || busy[`study:${item.pathway_id}:${item.task_id}`]} onClick={() => completeStudy(item.pathway_id, item.task_id)}>{busy[`study:${item.pathway_id}:${item.task_id}`] ? '保存中…' : '标记完成'}</button>}
                  </div>
                ))}</div>
              </section>
            )}
            <details className="desktop-mission-context"><summary>本周建议与身体状态</summary>
            {bodyContext && groups.some(group => group.key === 'body') && (
              <div className={`ms-health-context ${bodyContext.was_adjusted ? 'adjusted' : ''}`}>
                <div>
                  <strong>身体准备度 {bodyContext.readiness_score}</strong>
                  <span>{bodyContext.readiness_level} · {bodyContext.recommended_intensity} · {bodyContext.recommended_duration}</span>
                </div>
                {bodyContext.was_adjusted && <b>已自动降低难度</b>}
              </div>
            )}
            {report?.summary && (
              <section className="ms-report">
                <div className="ms-report-head"><span>上周分析 · 本周方向</span><b>{report.mode === 'ai' ? 'AI 生成' : '数据规则生成'}</b></div>
                <p>{report.summary}</p>
                {report.observation && <p>{report.observation}</p>}
                {report.suggestion && <small>{report.suggestion}</small>}
                {report.ai_status === 'quota_exhausted' && <small>AI 免费额度已用尽，本周暂按数据规则生成；恢复额度后可重新生成。</small>}
                {report.ai_status === 'unavailable' && <small>AI 暂时不可用，本周暂按数据规则生成。</small>}
                {snapshot?.profile?.assumed_fields?.length ? <small>年龄、身高或体重资料不全；缺失项使用默认规划假设，请在设置中补充。</small> : null}
              </section>
            )}
            </details>

          </>
        )}
      </main>

      <style>{`
        .dim-root { min-height: 100vh; background: #030712; color: #f8fafc; padding-bottom: 40px; }
        .dim-header-bar {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 20px; padding-top: calc(16px + var(--status-bar-height, 28px));
          background: rgba(15,23,42,0.95); border-bottom: 1px solid rgba(255,255,255,0.06);
          position: sticky; top: 0; z-index: 10;
        }
        .dim-header-bar h1 { font-size: 18px; margin: 0; font-weight: 700; }
        .dim-back-btn {
          width: 36px; height: 36px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.1);
          background: rgba(139,92,246,0.15); color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer;
        }
        .ms-settings-btn { min-width: 52px; height: 34px; padding: 0 12px; border-radius: 999px; border: 1px solid rgba(52,211,153,.35); background: rgba(16,185,129,.1); color: #6ee7b7; font-size: 12px; font-weight: 700; cursor: pointer; }
        .dim-main { padding: 16px; }
        .dim-loading, .dim-empty { text-align: center; padding: 48px 16px; color: #94a3b8; }
        .dim-spinner { width: 32px; height: 32px; border: 3px solid rgba(255,255,255,0.1); border-top-color: #8b5cf6; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 12px; }
        .ms-progress {
          background: linear-gradient(180deg, rgba(30,41,59,.82), rgba(15,23,42,.78));
          border: 1px solid rgba(148,163,184,0.12); border-radius: 16px; padding: 20px; margin-bottom: 20px;
        }
        .ms-notice { margin-bottom: 12px; padding: 10px 12px; border: 1px solid rgba(52,211,153,.22); border-radius: 10px; background: rgba(16,185,129,.08); color: #a7f3d0; font-size: 12px; }
        .ms-refreshing { margin-bottom:10px; color:#a7f3d0; font-size:12px; }
        .ms-task-reason { margin-top:6px; color:#b8c7df; font-size:12px; line-height:1.5; }
        .ms-task-progress-label { margin-top:6px; color:#a7f3d0; font-size:12px; line-height:1.5; }
        .ms-task-progress-track { height:5px; margin-top:7px; overflow:hidden; border-radius:6px; background:#334155; }
        .ms-task-progress-track i { display:block; height:100%; background:#34d399; }
        .ms-auto-label, .ms-archived { font-size:11px; color:#94a3b8; }
        .ms-done-btn:disabled { opacity:.55; cursor:default; }
        .ms-config-panel { min-width:0; margin:0; position: relative; overflow: hidden; padding: 20px; margin-bottom: 16px; border: 1px solid rgba(52,211,153,.2); border-radius: 20px; background: radial-gradient(circle at 100% 0, rgba(16,185,129,.14), transparent 38%), linear-gradient(160deg, rgba(15,23,42,.98), rgba(3,7,18,.96)); }
        .ms-config-panel h2 { margin: 3px 0 6px; font-size: 22px; }
        .ms-config-kicker { color: #34d399; font: 700 10px/1.2 ui-monospace, SFMono-Regular, Menlo, monospace; letter-spacing: .18em; }
        .ms-config-intro { margin: 0 0 20px; color: #94a3b8; font-size: 12px; line-height: 1.6; }
        .ms-field-label { display: block; margin: 16px 0 8px; color: #cbd5e1; font-size: 11px; font-weight: 700; letter-spacing: .08em; }
        .ms-dimension-grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 7px; }
        .ms-dimension-option { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 11px 4px; border: 1px solid rgba(148,163,184,.12); border-radius: 12px; background: rgba(30,41,59,.55); color: #64748b; cursor: pointer; }
        .ms-dimension-option.selected { background: rgba(15,23,42,.9); box-shadow: inset 0 0 18px rgba(255,255,255,.025); }
        .ms-dimension-option strong { font-size: 12px; }
        .ms-body-config { margin-top: 18px; padding-top: 18px; border-top: 1px dashed rgba(148,163,184,.18); }
        .ms-body-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; }
        .ms-body-heading div { display: flex; align-items: center; gap: 8px; }
        .ms-body-heading div span { font-size: 14px; font-weight: 800; }
        .ms-body-heading div strong { padding: 2px 7px; border-radius: 999px; background: rgba(34,197,94,.12); color: #86efac; font-size: 9px; }
        .ms-body-heading small { max-width: 210px; color: #64748b; font-size: 10px; text-align: right; line-height: 1.4; }
        .ms-focus-grid { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 8px; }
        .ms-choice-card { display: flex; flex-direction: column; gap: 4px; padding: 12px; border: 1px solid rgba(148,163,184,.12); border-radius: 12px; background: rgba(30,41,59,.45); color: #cbd5e1; text-align: left; cursor: pointer; }
        .ms-choice-card.selected { border-color: #34d399; background: rgba(16,185,129,.09); }
        .ms-choice-card strong { font-size: 13px; }
        .ms-choice-card span { color: #64748b; font-size: 10px; line-height: 1.4; }
        .ms-difficulty-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
        .ms-difficulty-row button { padding: 9px; border: 1px solid rgba(148,163,184,.12); border-radius: 10px; background: rgba(30,41,59,.5); color: #94a3b8; cursor: pointer; }
        .ms-difficulty-row button.selected { border-color: #f59e0b; background: rgba(245,158,11,.1); color: #fbbf24; }
        .ms-generate-btn { width: 100%; margin-top: 20px; padding: 12px; border: 0; border-radius: 12px; background: linear-gradient(135deg, #10b981, #0d9488); color: white; font-weight: 800; cursor: pointer; }
        .ms-generate-btn:disabled { opacity: .6; }
        .ms-health-context { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; padding: 12px 14px; border: 1px solid rgba(34,197,94,.18); border-radius: 12px; background: rgba(34,197,94,.06); }
        .ms-health-context > div { display: flex; flex-direction: column; gap: 2px; }
        .ms-health-context strong { font-size: 12px; color: #86efac; }
        .ms-health-context span { color: #94a3b8; font-size: 10px; }
        .ms-health-context b { flex-shrink: 0; color: #fbbf24; font-size: 10px; }
        .ms-report { margin-bottom: 16px; padding: 16px; border: 1px solid rgba(52,211,153,.24); border-radius: 16px; background: linear-gradient(150deg, rgba(6,78,59,.23), rgba(15,23,42,.9)); }
        .ms-report-head { display: flex; justify-content: space-between; color: #a7f3d0; font-weight: 800; font-size: 13px; }
        .ms-report-head b { font-size: 10px; opacity: .75; }
        .ms-report p { margin: 10px 0; color: #e2e8f0; font-size: 12px; line-height: 1.6; }
        .ms-report small { display: block; margin-top: 8px; color: #94a3b8; line-height: 1.5; }
        .ms-profile-fields { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
        .ms-profile-fields input, .ms-health-notes { box-sizing: border-box; min-width: 0; width: 100%; padding: 10px; border: 1px solid rgba(148,163,184,.25); border-radius: 9px; background: #0f172a; color: white; }
        .ms-health-notes { margin-top: 8px; }
        .ms-progress-top { display: flex; justify-content: space-between; margin-bottom: 10px; font-weight: 700; }
        .ms-nums { font-size: 22px; color: #34d399; }
        .ms-bar-track { height: 8px; background: rgba(30,41,59,0.8); border-radius: 4px; overflow: hidden; }
        .ms-bar-fill { height: 100%; background: linear-gradient(90deg, #22c55e, #34d399); transition: width 0.5s; }
        .ms-hint { font-size: 11px; color: #94a3b8; margin-top: 6px; text-align: right; }
        .ms-group { margin-bottom: 20px; }
        .ms-group-hdr { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; font-size: 14px; flex-wrap: wrap; }
        .ms-score { font-size: 11px; padding: 2px 8px; border-radius: 999px; background: rgba(255,255,255,0.06); color: #94a3b8; }
        .ms-score.low { background: rgba(239,68,68,0.1); color: #f87171; }
        .ms-boost { font-size: 10px; color: #f59e0b; }
        .ms-count { margin-left: auto; font-size: 12px; color: #94a3b8; }
        .ms-tasks { display: flex; flex-direction: column; gap: 8px; }
        .ms-task {
          display: flex; align-items: center; gap: 10px;
          background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.05);
          border-radius: 12px; padding: 12px 14px;
        }
        .ms-task.done { opacity: 0.55; }
        .ms-task-body { flex: 1; min-width: 0; }
        .ms-task-title { font-size: 13px; font-weight: 600; }
        .ms-task-desc { font-size: 11px; color: #94a3b8; margin-top: 2px; }
        .ms-exp { font-size: 9px; color: #ffd700; margin-top: 4px; display: inline-block; }
        .ms-done-btn {
          padding: 5px 14px; border-radius: 8px; font-size: 11px; font-weight: 600;
          background: linear-gradient(135deg, #8b5cf6, #6366f1); color: #fff; border: none; cursor: pointer;
        }
        .ms-done-btn:disabled { opacity: 0.6; }
        @media (max-width: 520px) {
          .ms-dimension-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          .ms-body-heading { flex-direction: column; }
          .ms-body-heading small { max-width: none; text-align: left; }
          .ms-health-context { align-items: flex-start; flex-direction: column; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default MissionCenter;
